import React, { useEffect, useRef, useState } from "react";
import { type WeddingLanguage, weddingContent } from "./content";
import firstTripImage from "../../assets/img/FirstTrip.JPG";
import howWeMetImage from "../../assets/img/HowWeMet.jpg";
import proposalImage from "../../assets/img/Proposal.png";
import wedding from "../../assets/img/our-moment-04.jpg";

interface OurStoryProps {
  language: WeddingLanguage;
}

export function OurStory({ language }: OurStoryProps) {
  const content = weddingContent[language].story;
  const cardRefs = useRef<Array<HTMLElement | null>>([]);
  const [visibleCards, setVisibleCards] = useState<Set<number>>(new Set());
  const milestoneImages = [
    howWeMetImage,
    firstTripImage,
    proposalImage,
    wedding,
  ];

  useEffect(() => {
    if (!("IntersectionObserver" in window)) {
      setVisibleCards(new Set(content.milestones.map((_, index) => index)));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          const target = entry.target as HTMLElement;
          const index = Number(target.dataset.storyIndex);

          setVisibleCards((prev) => {
            if (prev.has(index)) {
              return prev;
            }

            const next = new Set(prev);
            next.add(index);
            return next;
          });

          observer.unobserve(target);
        });
      },
      { threshold: 0.45, rootMargin: "0px 0px -18% 0px" },
    );

    cardRefs.current.forEach((card) => {
      if (card) {
        observer.observe(card);
      }
    });

    return () => observer.disconnect();
  }, [content.milestones]);

  return (
    <section id="story" className="wedding-section wedding-section--white">
      <div className="wedding-container">
        <div className="wedding-header">
          <h2 className="wedding-title">{content.heading}</h2>
          <div className="wedding-divider" />
        </div>

        <div className="story-grid">
          {content.milestones.map((milestone, index) => (
            <article
              key={index}
              ref={(element) => {
                cardRefs.current[index] = element;
              }}
              data-story-index={index}
              className={`story-card-shell ${
                visibleCards.has(index) ? "story-card-shell--visible" : ""
              }`}
            >
              <div
                className="story-card"
                style={{
                  backgroundImage: `linear-gradient(180deg, rgba(8, 8, 8, 0.06) 0%, rgba(8, 8, 8, 0.26) 58%, rgba(8, 8, 8, 0.64) 100%), url(${milestoneImages[index]})`,
                }}
              >
                <div className="story-card__content">
                  <h3 className="story-card__title">{milestone.year}</h3>
                  <p className="story-card__subtitle">{milestone.title}</p>
                  <p className="story-card__description">{milestone.description}</p>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}



